import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import styles from "./FindingDetails.module.scss";
import Button from "../../components/Button/Button";
import Input from "../../components/Input/Input";
import Textarea from "../../components/Textarea/Textarea";
import RaciAssignment from "../../components/RaciAssignment/RaciAssignment";
import PageLoader from "../../components/Loader/PageLoader";
import {
  ChevronDown,
  ArrowLeft,
  TrendingUp,
  Check,
  Save,
  Clock,
  User,
  Paperclip,
  FileText,
  Send,
  MessageSquare,
} from "lucide-react";
import classNames from "classnames";
import { Formik, Form, Field } from "formik";
import OutsideClickHandler from "react-outside-click-handler";
import axiosInstance from "../../utils/utils";
import { getOrganizationData } from "../../actions/organization";
import { showModal } from "../../redux/modalStore";
import LinkImprovementModal from "../../modals/LinkImprovementModal";
import CascadingDropdown from "../../components/CascadingDropdown/CascadingDropdown";
import { handleExport } from "../../utils/helpers";
import FormikUpload from "../../components/FormikComponents/FormikUpload/FormikUpload";

const statusOptions = [
  { id: "Open", label: "Open", tone: "open" },
  { id: "In Progress", label: "In Progress", tone: "progress" },
  { id: "In Review", label: "In Review", tone: "review" },
  { id: "Done", label: "Done", tone: "done" },
];

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value) return [value];
  return [];
};

const normalizeAttachments = (attachments = []) =>
  attachments
    .map((item) => (typeof item === "string" ? item : item?.url || item?.link || ""))
    .filter(Boolean);

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const getRelativeTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 45) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  const diffHours = Math.round(diffMin / 60);
  if (diffHours < 24) {
    return `about ${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  }
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  const diffWeeks = Math.round(diffDays / 7);
  if (diffWeeks < 5) return `${diffWeeks} week${diffWeeks === 1 ? "" : "s"} ago`;
  const diffMonths = Math.round(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} month${diffMonths === 1 ? "" : "s"} ago`;
  const diffYears = Math.round(diffDays / 365);
  return `${diffYears} year${diffYears === 1 ? "" : "s"} ago`;
};

const formatFileSize = (bytes) => {
  if (!bytes || Number.isNaN(bytes)) return "";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = Number(bytes);
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size < 10 && unitIndex > 0 ? 1 : 0)} ${units[unitIndex]}`;
};

const parseUpdateBody = (body) => {
  if (!body) return [];
  const rawText = typeof body === "string" ? body : String(body);
  const lines = rawText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => (line.startsWith("- ") ? line.slice(2).trim() : line));

  return lines.map((line) => {
    const colonIndex = line.indexOf(":");
    if (colonIndex > 0) {
      const label = line.slice(0, colonIndex).trim();
      const value = line.slice(colonIndex + 1).trim();
      if (label) {
        return { label, value };
      }
    }
    return { text: line };
  });
};

const getUpdateTimestamp = (update) =>
  update?.date ||
  update?.updated_at ||
  update?.updatedAt ||
  update?.updated_on ||
  update?.updatedOn ||
  update?.created_at ||
  update?.createdAt ||
  update?.created_on ||
  update?.createdOn;

const getUpdateBodyText = (update) => {
  if (!update?.body) return "";
  return typeof update.body === "string" ? update.body : String(update.body);
};

const getUpdateAuthor = (update) => {
  const candidate =
    update?.author || update?.user || update?.created_by || update?.createdBy;
  if (typeof candidate === "string") return candidate;
  if (candidate && typeof candidate === "object") {
    return candidate.email || candidate.name || "";
  }
  return "";
};

const normalizeUpdateAttachments = (attachments = []) => {
  const items = Array.isArray(attachments) ? attachments : [attachments];
  return items
    .map((attachment) => {
      if (!attachment) return null;
      if (typeof attachment === "string") {
        return {
          url: attachment,
          filename: attachment.split("/").pop() || "Attachment",
        };
      }
      const url = attachment.url || attachment.path || attachment.link || "";
      const filename =
        attachment.filename ||
        attachment.name ||
        (url ? url.split("/").pop() : "") ||
        "Attachment";
      return { url, filename };
    })
    .filter((attachment) => attachment && attachment.url);
};

const isStructuredUpdate = (update) => {
  const body = getUpdateBodyText(update).trim();
  if (!body) return false;
  return parseUpdateBody(body).some((item) => item.label);
};

const isCommentUpdate = (update) => {
  const body = getUpdateBodyText(update).trim();
  if (body) return !isStructuredUpdate(update);
  return normalizeUpdateAttachments(update?.attachments || []).length > 0;
};

const FormStateBridge = ({ dirty, isSubmitting, onChange }) => {
  useEffect(() => {
    onChange(dirty, isSubmitting);
  }, [dirty, isSubmitting, onChange]);

  return null;
};

const FindingDetails = () => {
  const { orgId, findingKey } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const location = useLocation();

  const { findingsData, improvementsData, risksData } = useSelector(
    (state) => state.organization,
  );
  const userEmail = useSelector((state) => state.user.email);

  console.log(findingsData);

  const finding = useMemo(
    () => Object.values(findingsData || {}).find((item) => item.key === findingKey),
    [findingKey, findingsData],
  );
  const hasLoadedFindings = Object.keys(findingsData || {}).length > 0;

  const [activeTab, setActiveTab] = useState("details");
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [updates, setUpdates] = useState([]);
  const sessionToken = localStorage.getItem("session_token");
  const handleFormState = useCallback((dirty, submitting) => {
    setIsDirty(dirty);
    setIsSubmitting(submitting);
  }, []);

  const sortedRisks = useMemo(() => {
    const risksArray = Object.values(risksData || {});
    return [...risksArray].sort((a, b) =>
      String(a.risk_id || "").localeCompare(String(b.risk_id || "")),
    );
  }, [risksData]);

  const cascadingRiskOptions = useMemo(
    () =>
      sortedRisks
        .filter((risk) => risk.level?.toLowerCase() !== "none")
        .map((risk) => ({
          id: risk.risk_num,
          reference: risk.risk_id,
          label: `${risk.risk_id} ${risk.description}`,
        })),
    [sortedRisks],
  );

  const cascadingChapterOptions = useMemo(() => {
    const validChapterIds = new Set(
      cascadingRiskOptions
        .map((risk) => String(risk.reference || "").split("-")[0])
        .filter(Boolean),
    );

    return Array.from(validChapterIds)
      .map((chapterId) => {
        const matchingRisk = sortedRisks.find(
          (risk) => String(risk.qms_id) === chapterId,
        );
        return {
          id: chapterId,
          label: `${chapterId} ${matchingRisk?.qms_description || ""}`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [cascadingRiskOptions, sortedRisks]);

  useEffect(() => {
    const nextUpdates = Array.isArray(finding?.updates) ? finding.updates : [];
    setUpdates(nextUpdates);
  }, [finding?.updates]);

  if (!finding && hasLoadedFindings) {
    return (
      <MainLayout>
        <div className={styles.notFound}>
          <div className={styles.emptyState}>We could not find this finding.</div>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate(`/${orgId}/findings${location.search}`)}
          >
            <ArrowLeft size={18} />
            Back to Findings
          </button>
        </div>
      </MainLayout>
    );
  }

  if (!finding) {
    return (
      <MainLayout>
        <PageLoader text="Loading finding..." pageLoader />
      </MainLayout>
    );
  }

  const existingAttachments = finding.attachments || [];
  const initialValues = {
    reference: finding.reference || "",
    description: finding.description || "",
    responsible: Array.isArray(finding.responsible)
      ? finding.responsible.join(", ")
      : finding.responsible || "",
    accountable: normalizeArray(finding.accountable),
    consulted: normalizeArray(finding.consulted),
    informed: normalizeArray(finding.informed),
    risk: finding.risk?.risk_num ? String(finding.risk.risk_num) : "",
    attachments: normalizeAttachments(existingAttachments),
    status: finding.status || "Open",
  };

  const handleSave = async (values, formikHelpers) => {
    const selectedRisk = cascadingRiskOptions.find(
      (opt) => String(opt.id) === String(values.risk),
    );
    const payload = {};

    const originalAttachments = normalizeAttachments(existingAttachments);

    const cleanedResponsible = values.responsible?.trim?.() || "";

    if ((finding.reference || "") !== values.reference) {
      payload.reference = values.reference;
    }
    if ((finding.description || "") !== values.description) {
      payload.description = values.description;
    }
    if ((finding.status || "") !== values.status) {
      payload.status = values.status;
    }
    const currentResponsible = Array.isArray(finding.responsible)
      ? (finding.responsible[0] || "")
      : (finding.responsible || "");
    if (currentResponsible !== cleanedResponsible) {
      payload.responsible = cleanedResponsible;
    }

    ["accountable", "consulted", "informed"].forEach((field) => {
      const current = normalizeArray(finding[field]);
      const next = normalizeArray(values[field])
        .map((x) => (typeof x === "string" ? x.trim() : ""))
        .filter(Boolean);
      if (JSON.stringify(current) !== JSON.stringify(next)) {
        payload[field] = next;
      }
    });

    const nextAttachments = values.attachments || [];
    if (JSON.stringify(originalAttachments) !== JSON.stringify(nextAttachments)) {
      payload.attachments = nextAttachments;
    }

    if (selectedRisk) {
      const currentRisk = finding.risk?.risk_num || "";
      if (String(currentRisk) !== String(selectedRisk.id || "")) {
        payload.risk = String(selectedRisk.id);
      }
    }

    if (Object.keys(payload).length === 0) {
      formikHelpers.setSubmitting(false);
      return;
    }

    const requestBody = [
      {
        id: finding.id,
        ...payload,
      },
    ];

    try {
      await axiosInstance.patch(`/nocodb/findings?org_id=${orgId}`, requestBody, {
        headers: {
          "Content-Type": "application/json",
          "session-token": sessionToken,
        },
      });

      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          true,
          true,
          true,
          false,
          false,
        ),
      );
    } catch (error) {
      console.error("Failed to save finding", error);
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  const handleUpdateSubmit = async (
    values,
    { resetForm, setFieldValue, setSubmitting },
  ) => {
    try {
      const requestBody = {
        body: values.body,
        attachments: (values.attachments || []).map((attachment) => attachment),
        finding: finding?.id || 0,
        improvement: 0,
        audit_task: 0,
      };

      const response = await axiosInstance.post(
        `/nocodb/update?org_id=${orgId}`,
        requestBody,
        {
          headers: {
            "session-token": sessionToken,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status === 200) {
        const transformedAttachments = normalizeUpdateAttachments(
          values.attachments || [],
        );
        setUpdates((prevUpdates) => [
          {
            author: userEmail,
            date: new Date().toISOString(),
            body: values.body,
            attachments: transformedAttachments,
          },
          ...prevUpdates,
        ]);
        setFieldValue("attachments", []);

        await dispatch(
          getOrganizationData(
            orgId,
            sessionToken,
            null,
            false,
            true,
            true,
            true,
            false,
            false,
          ),
        );

        resetForm();
      }
    } catch (error) {
      console.error("Error adding update:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLinkedImprovementClick = (improvementKey) => {
    if (!improvementKey) return;
    navigate(
      `/${orgId}/improvement-projects/${improvementKey}${location.search}`,
    );
  };

  const linkedImprovementEntries = (finding.linked_improvements || []).map((item) => {
    const key = typeof item === "string" || typeof item === "number" ? item : item?.key;
    return {
      key,
      data:
        (typeof item === "object" && item) ||
        improvementsData?.[key] ||
        improvementsData?.[item],
    };
  });
  const lastUpdatedAt = updates.reduce((latest, update) => {
    const candidate = getUpdateTimestamp(update);
    if (!candidate) return latest;
    if (!latest) return candidate;
    const candidateTime = new Date(candidate).getTime();
    const latestTime = new Date(latest).getTime();
    if (Number.isNaN(candidateTime)) return latest;
    if (Number.isNaN(latestTime) || candidateTime > latestTime) return candidate;
    return latest;
  }, "");
  const showMetaCard = Boolean(lastUpdatedAt);
  const initialHoveredOption =
    finding.risk?.qms_id ||
    (finding.risk?.risk_id ? String(finding.risk.risk_id).split("-")[0] : null);

  const renderTabContent = (tab, values) => {
    if (tab === "attachments") {
      return (
        <div className={styles.attachmentsTab}>
          <div className={styles.attachmentsHeading}>Attachments</div>
          {existingAttachments.length > 0 ? (
            <div className={styles.existingAttachments}>
              {existingAttachments.map((attachment, index) => {
                const url = attachment.url || attachment.link || attachment.path || attachment;
                const label =
                  attachment.filename ||
                  attachment.name ||
                  (typeof attachment === "string"
                    ? attachment.split("/").pop()
                    : "Attachment");
                const sizeValue =
                  attachment.size ||
                  attachment.file_size ||
                  attachment.filesize ||
                  attachment.fileSize;
                const sizeLabel = sizeValue ? formatFileSize(sizeValue) : "";

                return (
                  <a
                    key={`${url}-${index}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.existingAttachmentItem}
                  >
                    <div className={styles.existingAttachmentInfo}>
                      <Paperclip size={16} className={styles.existingAttachmentIcon} />
                      <div className={styles.existingAttachmentText}>
                        <div className={styles.existingAttachmentName}>{label}</div>
                        {sizeLabel && (
                          <div className={styles.existingAttachmentSize}>{sizeLabel}</div>
                        )}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          ) : (
            <div className={styles.emptyState}>No attachments uploaded yet.</div>
          )}
        </div>
      );
    }

    if (tab === "audit-report") {
      return <div className={styles.emptyState}>Coming soon.</div>;
    }

    if (tab === "comments") {
      const commentEntries = updates
        .filter(isCommentUpdate)
        .map((update) => {
          const attachments = normalizeUpdateAttachments(
            update?.attachments || [],
          );
          return {
            date: getUpdateTimestamp(update),
            author: getUpdateAuthor(update),
            body: getUpdateBodyText(update).trim(),
            attachments,
          };
        })
        .filter((entry) => entry.body || entry.attachments.length)
        .sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB - dateA;
        });

      if (!commentEntries.length) {
        return <div className={styles.emptyState}>No comments yet.</div>;
      }

      return (
        <div className={styles.commentsCard}>
          <div className={styles.commentsHeader}>
            <div className={styles.commentsTitleRow}>
              <MessageSquare size={20} />
              <div className={styles.commentsTitle}>Comments</div>
            </div>
            <div className={styles.commentsSubtitle}>
              Notes and attachments added by the team
            </div>
          </div>
          <div className={styles.commentsList}>
            {commentEntries.map((entry, idx) => {
              const relative = entry.date ? getRelativeTime(entry.date) : "";
              const timestamp = entry.date ? formatDateTime(entry.date) : "";
              const author = entry.author || "Unknown";

              return (
                <div key={`${author}-${entry.date || idx}`} className={styles.commentItem}>
                  <div className={styles.commentMetaRow}>
                    <div className={styles.commentAuthor}>{author}</div>
                    {relative && <div className={styles.commentTime}>{relative}</div>}
                  </div>
                  {timestamp && (
                    <div className={styles.commentTimestamp}>{timestamp}</div>
                  )}
                  {entry.body && (
                    <div className={styles.commentBody}>{entry.body}</div>
                  )}
                  {entry.attachments.length > 0 && (
                    <div className={styles.commentAttachments}>
                      {entry.attachments.map((attachment, attachmentIndex) => (
                        <a
                          key={`${attachment.url}-${attachmentIndex}`}
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.commentAttachment}
                        >
                          <Paperclip size={14} />
                          <span>{attachment.filename}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (tab === "change-history") {
      const historyEntries = updates
        .filter(isStructuredUpdate)
        .map((update) => ({
          type: "Updated",
          date: getUpdateTimestamp(update),
          author: getUpdateAuthor(update),
          body: getUpdateBodyText(update) || "Updated record",
        }));

      const sortedEntries = historyEntries
        .filter((entry) => entry.date || entry.author || entry.body)
        .sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB - dateA;
        });

      if (!sortedEntries.length) {
        return <div className={styles.emptyState}>No changes yet.</div>;
      }
      return (
        <div className={styles.changeHistoryCard}>
          <div className={styles.changeHistoryHeader}>
            <div className={styles.changeHistoryTitleRow}>
              <Clock size={20} />
              <div className={styles.changeHistoryTitle}>Change History</div>
            </div>
            <div className={styles.changeHistorySubtitle}>
              Track of all changes made to this record
            </div>
          </div>
          <div className={styles.changeHistoryList}>
            {sortedEntries.map((entry, idx) => {
              const relative = entry.date ? getRelativeTime(entry.date) : "";
              const timestamp = entry.date ? formatDateTime(entry.date) : "";
              const rawBody =
                entry.body && typeof entry.body !== "string"
                  ? String(entry.body)
                  : entry.body || "";
              const parsedBody = rawBody ? parseUpdateBody(rawBody) : [];
              const hasStructuredBody =
                parsedBody.length > 1 || parsedBody.some((item) => item.label);
              return (
                <div
                  key={`${entry.type}-${idx}`}
                  className={classNames(styles.changeHistoryItem, styles.historyUpdated)}
                >
                  <div className={styles.changeHistoryMetaRow}>
                    <div className={styles.changeHistoryMetaLeft}>
                      <div
                        className={classNames(
                          styles.changeHistoryBadge,
                          styles.badgeUpdated,
                        )}
                      >
                        {entry.type}
                      </div>
                      {relative && (
                        <span className={styles.changeHistoryRelative}>
                          {relative}
                        </span>
                      )}
                    </div>
                    {timestamp && (
                      <span className={styles.changeHistoryTimestamp}>
                        {timestamp}
                      </span>
                    )}
                  </div>
                  {(entry.author || entry.body) && (
                    <div className={styles.changeHistoryDetails}>
                      {entry.author && (
                        <div className={styles.changeHistoryAuthor}>
                          <User size={14} />
                          <span>{entry.author}</span>
                        </div>
                      )}
                      {entry.body && (
                        <div className={styles.changeHistoryBody}>
                          {!hasStructuredBody ? (
                            <span className={styles.changeHistoryBodyText}>
                              {rawBody}
                            </span>
                          ) : (
                            <ul className={styles.changeHistoryBodyList}>
                              {parsedBody.map((item, itemIndex) => (
                                <li
                                  key={`${entry.type}-${idx}-${itemIndex}`}
                                  className={styles.changeHistoryBodyItem}
                                >
                                  {item.label ? (
                                    <>
                                      <span className={styles.changeHistoryBodyLabel}>
                                        {item.label}
                                      </span>
                                      <span className={styles.changeHistoryBodyValue}>
                                        {item.value || "Not set"}
                                      </span>
                                    </>
                                  ) : (
                                    <span className={styles.changeHistoryBodyText}>
                                      {item.text}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className={styles.detailsGrid}>
        <div className={styles.fieldBlock}>
          <label htmlFor="reference">Reference</label>
          <Field name="reference" component={Input} placeholder="Reference" />
        </div>

        <div className={classNames(styles.fieldBlock, styles.fullWidth)}>
          <label htmlFor="description">Description</label>
          <Field name="description" component={Textarea} placeholder="Description" />
        </div>

        <div className={classNames(styles.fieldBlock, styles.fullWidth)}>
          <Field
            name="risk"
            component={CascadingDropdown}
            label="Related Risk Profile"
            text="Search and select risk profile..."
            options={cascadingChapterOptions}
            suboptions={cascadingRiskOptions}
            selectedValues={[values?.risk]}
            initialHoveredOption={initialHoveredOption}
            className={styles.riskDropdown}
            openDirection="up"
          />
        </div>

      </div>
    );
  };

  return (
    <MainLayout>
      <div className={styles.page}>
        <div className={styles.topBar}>
          <Button
            type="button"
            className={styles.backButton}
            onClick={() => navigate(`/${orgId}/findings${location.search}`)}
            text="Back to Findings"
            icon={<ArrowLeft size={16} />}
            iconPosition="left"
          />

          <div className={styles.actions}>
            <Button
              text="Add to Improvement"
              className={styles.secondaryAction}
              icon={<TrendingUp size={16} />}
              iconPosition="left"
              onClick={() => dispatch(showModal({ name: "link_improvement_modal", data: finding }))}
            />
            <Button
              text={isExporting ? "Exporting..." : "Word"}
              className={styles.secondaryAction}
              icon={<FileText size={16} />}
              iconPosition="left"
              onClick={async () => {
                if (isExporting) return;
                setIsExporting(true);
                await handleExport({
                  selectedItems: [finding],
                  orgId,
                  sessionToken,
                  getKey: (item) => item.key,
                  filePrefix: "Finding",
                });
                setIsExporting(false);
              }}
              disabled={isExporting}
            />
            <Button
              text="Save Changes"
              className={styles.primaryAction}
              onClick={() => formikRef.current?.submitForm()}
              disabled={isSubmitting || !isDirty}
              icon={<Save size={16} />}
              iconPosition="left"
            />
          </div>
        </div>

        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          enableReinitialize
          onSubmit={(values, helpers) => {
            helpers.setSubmitting(true);
            handleSave(values, helpers);
          }}
        >
          {({ values, dirty, isSubmitting }) => {
            const statusTone =
              statusOptions.find((option) => option.id === values.status)?.tone || "open";

            return (
              <Form className={styles.form}>
                <FormStateBridge
                  dirty={dirty}
                  isSubmitting={isSubmitting}
                  onChange={handleFormState}
                />
                <div className={styles.headerCard}>
                  <div className={styles.headerTop}>
                    <div className={styles.keyPill}>{finding.key}</div>
                    <div className={styles.statusBlock}>
                      <div className={styles.statusLabel}>Status</div>
                      <OutsideClickHandler
                        onOutsideClick={() => setIsStatusOpen(false)}
                        disabled={!isStatusOpen}
                      >
                        <div className={styles.statusDropdown}>
                          <button
                            type="button"
                            className={classNames(
                              styles.statusSelect,
                              styles[`status-${statusTone}`],
                            )}
                            onClick={() => setIsStatusOpen((prev) => !prev)}
                          >
                            <span>{values.status}</span>
                            <ChevronDown size={16} />
                          </button>
                          {isStatusOpen && (
                            <div className={styles.statusMenu}>
                              {statusOptions.map((option) => (
                                <button
                                  key={option.id}
                                  type="button"
                                  className={classNames(styles.statusOption, {
                                    [styles.activeStatus]: values.status === option.id,
                                  })}
                                  onClick={() => {
                                    formikRef.current?.setFieldValue("status", option.id);
                                    setIsStatusOpen(false);
                                  }}
                                >
                                  <span className={styles.statusCheckSlot}>
                                    {values.status === option.id && <Check size={14} />}
                                  </span>
                                  <div
                                    className={classNames(
                                      styles.statusDot,
                                      styles[`dot-${option.tone}`],
                                    )}
                                  />
                                  <span className={styles.statusOptionLabel}>
                                    {option.label}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </OutsideClickHandler>
                    </div>
                  </div>
                </div>

                <div className={styles.bodyGrid}>
                  <div className={styles.raciSection}>
                    <div className={styles.sectionLabel}>RACI assignment</div>
                    <RaciAssignment />
                  </div>

                  <div className={styles.mainColumn}>
                    <div className={styles.tabHeaderRow}>
                      <div className={styles.tabs}>
                        <button
                          type="button"
                          className={classNames(styles.tabButton, {
                            [styles.activeTab]: activeTab === "details",
                          })}
                          onClick={() => setActiveTab("details")}
                        >
                          Details
                        </button>
                        <button
                          type="button"
                          className={classNames(styles.tabButton, {
                            [styles.activeTab]: activeTab === "audit-report",
                          })}
                          onClick={() => setActiveTab("audit-report")}
                        >
                          Audit Report
                        </button>
                        <button
                          type="button"
                          className={classNames(styles.tabButton, {
                            [styles.activeTab]: activeTab === "attachments",
                          })}
                          onClick={() => setActiveTab("attachments")}
                        >
                          Attachments
                        </button>
                        <button
                          type="button"
                          className={classNames(styles.tabButton, {
                            [styles.activeTab]: activeTab === "change-history",
                          })}
                          onClick={() => setActiveTab("change-history")}
                        >
                          Change History
                        </button>
                        <button
                          type="button"
                          className={classNames(styles.tabButton, {
                            [styles.activeTab]: activeTab === "comments",
                          })}
                          onClick={() => setActiveTab("comments")}
                        >
                          Comments
                        </button>
                      </div>
                    </div>

                    <div className={styles.tabPanel}>
                      {renderTabContent(activeTab, values)}
                    </div>
                  </div>

                  <div className={styles.sideColumn}>
                    <div className={styles.communicationCard}>
                      <div className={styles.communicationHeader}>
                        <div>
                          <div className={styles.communicationTitle}>
                            Communication Module
                          </div>
                          <div className={styles.communicationSubtitle}>
                            Add comments for this finding.
                          </div>
                        </div>
                      </div>
                      <Formik
                        initialValues={{ body: "", attachments: [] }}
                        onSubmit={handleUpdateSubmit}
                      >
                        {({ values, isSubmitting, submitForm }) => (
                          <div className={styles.communicationForm}>
                            {userEmail && (
                              <div className={styles.communicationUser}>
                                {userEmail}
                              </div>
                            )}
                            <Field
                              name="body"
                              component={Textarea}
                              minHeight="90px"
                              className={styles.communicationTextarea}
                              placeholder="Add a comment..."
                            />
                            <Field
                              name="attachments"
                              component={FormikUpload}
                              greenIcon
                              className={styles.communicationUpload}
                              labelClassName={styles.communicationUploadLabel}
                              labelContent={
                                <span className={styles.communicationUploadContent}>
                                  <Paperclip
                                    size={14}
                                    className={styles.communicationUploadIcon}
                                  />
                                  <span className={styles.communicationUploadText}>
                                    Attach files
                                  </span>
                                  <span className={styles.communicationUploadHint}>
                                    PDF, PNG, DOC
                                  </span>
                                </span>
                              }
                            />
                            <Button
                              text={isSubmitting ? "Posting..." : "Post comment"}
                              className={styles.communicationSubmit}
                              icon={<Send size={14} />}
                              iconPosition="right"
                              type="button"
                              onClick={submitForm}
                              disabled={
                                isSubmitting ||
                                (!values.body.trim() &&
                                  (values.attachments || []).length === 0)
                              }
                            />
                          </div>
                        )}
                      </Formik>
                    </div>
                    <div className={styles.sideCard}>
                      <div className={styles.sideCardHeader}>Linked Improvement Project</div>
                      {linkedImprovementEntries.length ? (
                        <>
                          <div className={styles.improvementList}>
                            {linkedImprovementEntries.map(({ key, data }) => {
                              const title =
                                data?.title ||
                                data?.name ||
                                data?.description ||
                                data?.summary ||
                                "Improvement";
                              const stage =
                                data?.stage ||
                                data?.phase ||
                                data?.lifecycle_stage ||
                                data?.lifecycle;
                              const status = data?.status || data?.state;
                              const meta = [stage, status]
                                .filter(Boolean)
                                .map((value) => String(value).replace(/_/g, " "))
                                .join(" • ");
                              return (
                                <button
                                  key={key}
                                  type="button"
                                  className={styles.improvementItem}
                                  onClick={() => handleLinkedImprovementClick(key)}
                                >
                                  <div className={styles.improvementCardHeader}>
                                    <div className={styles.improvementTitle}>{title}</div>
                                    <div className={styles.improvementKeyPill}>{key}</div>
                                  </div>
                                  {meta && <div className={styles.improvementMeta}>{meta}</div>}
                                </button>
                              );
                            })}
                          </div>
                        </>
                      ) : (
                        <Button
                          text="Link to Improvement"
                          className={styles.linkButton}
                          icon={<TrendingUp size={14} />}
                          iconPosition="left"
                          type="button"
                          onClick={() =>
                            dispatch(
                              showModal({
                                name: "link_improvement_modal",
                                data: finding,
                              }),
                            )
                          }
                        />
                      )}
                    </div>
                    {showMetaCard && (
                      <div className={classNames(styles.sideCard, styles.metaCard)}>
                        <div className={styles.metaGrid}>
                          <div>
                            <div className={styles.metaLabel}>Last updated</div>
                            <div className={styles.metaValue}>
                              {lastUpdatedAt ? formatDateTime(lastUpdatedAt) : "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
      <LinkImprovementModal />
    </MainLayout>
  );
};

export default FindingDetails;
